import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, setSentinelLogoutUrl, attemptBootRefresh, getRefreshToken } from '../utils/auth';
import { resolveTenant, UNKNOWN_TENANT, isDemoRegistrationSite, getAuthBaseUrl } from '../utils/tenant';
import type { TenantContext } from '../utils/tenant';
import { dbgV, dbgI, dbgW, dbgE, dbgTimer, rkLog } from '../utils/debug';
import { getSiteConfig } from '../utils/site';
import { Wordmark } from '../components/Wordmark';
import { issueState } from '../utils/oidcState';

const COMP = 'Login';

/**
 * Login page — delegates PKCE generation to the backend SDK, then redirects
 * to the OIDC provider (Sentinel Auth) for authentication.
 *
 * Flow: GET /api/auth/login → backend generates PKCE → returns authorize_url
 *       + state + code_verifier → store in sessionStorage → redirect to Sentinel.
 * After auth, Sentinel redirects back to /auth/callback (AuthCallback.tsx).
 */
export default function Login() {
  const navigate = useNavigate();
  const tenantResult = resolveTenant();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Run-once guard: React StrictMode double-invokes effects in dev, and a
  // double-initiation issues two OIDC flows a few ms apart. With per-flow state
  // keying (utils/oidcState) that's now HARMLESS to CSRF validation, but there's
  // no reason to fire two backend /api/auth/login calls + two redirects, so we
  // still guard the initiation to exactly once per mount.
  const initiatedRef = useRef(false);

  dbgV(COMP, 'component rendering', { loadingState: loading, hasError: !!error });

  useEffect(() => {
    dbgV(COMP, 'useEffect: mount');

    if (isDemoRegistrationSite()) {
      dbgI(COMP, 'demo registration site detected — redirecting to /demo/register');
      navigate('/demo/register', { replace: true });
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    async function boot() {
      dbgV(COMP, 'checking isAuthenticated before initiating OIDC');
      if (isAuthenticated()) {
        dbgI(COMP, 'user already authenticated — redirecting to /');
        navigate('/', { replace: true });
        return;
      }

      // Issue #1508: a direct load of /login (a bookmark, or a stale tab
      // reopened) can land here with the 5-minute access token expired but a
      // still-valid 12-hour refresh token sitting unused in localStorage. Try
      // it silently before committing to a full OIDC/password re-login —
      // mirrors the boot-refresh gate in App.tsx's Protected/PublicHome (and
      // PR #637 for the dispatcher). attemptBootRefresh() makes no network
      // call when there's no refresh token, so that path is unchanged.
      if (getRefreshToken()) {
        dbgI(COMP, 'expired access token + refresh token present — attempting silent refresh before OIDC redirect');
        const ok = await attemptBootRefresh();
        if (cancelled) return;
        if (ok) {
          dbgI(COMP, 'silent refresh succeeded — redirecting to /');
          navigate('/', { replace: true });
          return;
        }
        dbgW(COMP, 'silent refresh failed — falling through to OIDC login');
      }

      if (initiatedRef.current) {
        dbgV(COMP, 'OIDC login already initiated this mount — skipping duplicate (StrictMode)');
        return;
      }
      initiatedRef.current = true;
      dbgI(COMP, 'user not authenticated — initiating OIDC login flow');
      initiateOidcLogin();

      // Timeout fallback: if redirect doesn't happen in 15 seconds, show error
      timeoutId = setTimeout(() => {
        if (loading) {
          dbgW(COMP, 'OIDC redirect timeout (15s) — showing error to user');
          setError('Authentication is taking longer than expected. Please check your connection and try again.');
          setLoading(false);
        }
      }, 15000);
    }

    boot();

    return () => { cancelled = true; if (timeoutId) clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  async function initiateOidcLogin() {
    const timer = dbgTimer(COMP);
    try {
      const redirectUri = `${getAuthBaseUrl()}/auth/callback`;
      dbgI(COMP, 'initiateOidcLogin: calling GET /api/auth/login', { redirectUri });

      const res = await fetch(`/api/auth/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      timer('GET /api/auth/login responded');
      dbgI(COMP, '/api/auth/login response', { status: res.status, ok: res.ok });

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        dbgE(COMP, '/api/auth/login failed', { status: res.status, body: body.substring(0, 200) });
        throw new Error(`Failed to start authentication (${res.status})`);
      }

      const data = await res.json();
      dbgI(COMP, '/api/auth/login data received', {
        hasAuthorizeUrl: !!data.authorize_url,
        hasState: !!data.state,
        authorizeUrlPrefix: data.authorize_url?.substring(0, 60),
      });

      if (!data.authorize_url || !data.state) {
        dbgE(COMP, 'incomplete /api/auth/login response', {
          keys: Object.keys(data),
        });
        throw new Error('Auth configuration error: incomplete login response from backend');
      }

      // Derive issuer from authorize_url for logout URL fallback.
      // authorize_url is like https://alfred.quigs.com/oidc/authorize
      // Persist to localStorage (survives page refresh) AND sessionStorage
      // so getSentinelLogoutUrl() never falls back to the hardcoded dev URL.
      try {
        const url = new URL(data.authorize_url);
        const issuer = url.origin;
        dbgV(COMP, 'derived issuer from authorize_url', { issuer });
        sessionStorage.setItem('oidc_issuer', issuer);
        localStorage.setItem('oidc_issuer', issuer);
        setSentinelLogoutUrl(`${issuer}/oidc/end_session`);
      } catch {
        dbgW(COMP, 'could not derive issuer from authorize_url');
      }

      // Store state for CSRF validation at callback under a PER-FLOW key
      // (oidc_state:<state>) in BOTH sessionStorage and localStorage. Per-flow
      // keying means a re-initiated or concurrent flow can't clobber this one
      // (the root cause of #287's intermittent silent bounce); localStorage is
      // the durable fallback for the cross-host dev->IdP->dev PWA round-trip
      // where sessionStorage can be partitioned/dropped. AuthCallback validates
      // by membership (was this returned state issued?), not by equality against
      // a single mutable slot. See utils/oidcState.
      issueState(data.state, [sessionStorage, localStorage], Date.now());
      dbgV(COMP, 'OIDC state issued (per-flow key) to session+localStorage', {
        statePrefix: String(data.state).substring(0, 16),
      });

      dbgI(COMP, 'redirecting to OIDC provider', {
        authorizeUrl: data.authorize_url.substring(0, 120) + '...',
        redirectUri,
      });
      timer('redirecting to OIDC provider');

      window.location.href = data.authorize_url;
    } catch (err) {
      dbgE(COMP, 'initiateOidcLogin: error', { error: String(err), stack: err instanceof Error ? err.stack : undefined });
      setError(err instanceof Error ? err.message : 'Failed to start sign-in');
      setLoading(false);
    }
  }

  const cardStyle: React.CSSProperties = {
    background: b.cardBg, border: `1px solid ${b.border}`,
    borderRadius: b.radius, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    // Mobile-first: padding scales with viewport — comfortable on phones (24px on
    // a 360px screen), roomier on larger ones (up to 32/28). clamp() keeps the
    // card from feeling cramped on tiny devices or sparse on tablets/desktop.
    padding: 'clamp(24px, 7vw, 32px) clamp(20px, 6vw, 28px)', textAlign: 'center',
  };

  if (tenantResult === UNKNOWN_TENANT) {
    return (
      <Outer>
        <Header tenant={null} />
        <div style={cardStyle}>
          <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Department not found</p>
          <p style={{ color: b.textMuted, fontSize: 14, lineHeight: 1.6 }}>
            This portal address isn&apos;t associated with any department.
          </p>
        </div>
        <Footer />
      </Outer>
    );
  }

  const tenant = tenantResult;

  if (error) {
    dbgW(COMP, 'rendering error state', { error });
    return (
      <Outer>
        <Header tenant={tenant} />
        <div style={cardStyle}>
          <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>Sign-in error</p>
          <p style={{ color: b.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
            {error}
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => { rkLog('clicked:', 'Login', 'Try again -> initiateOidcLogin'); setError(''); setLoading(true); initiateOidcLogin(); }}
            style={{
              // .btn-primary supplies background/hover/disabled/radius/44px-min + font;
              // these inline props just make it full-width and touch-snappy on phones.
              padding: '12px 24px', fontWeight: 600,
              width: '100%', touchAction: 'manipulation',
            }}
          >
            Try again
          </button>
        </div>
        <Footer />
      </Outer>
    );
  }

  return (
    <Outer>
      <Header tenant={tenant} />
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <div className="login-spinner" style={{
            width: 20, height: 20, borderRadius: '50%',
            border: `2px solid ${b.accent}`, borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ color: b.textMuted, fontSize: 14, margin: 0 }}>
            {loading ? 'Redirecting to sign in...' : 'Loading...'}
          </p>
        </div>
        {loading && (
          <p style={{ color: b.textDim, fontSize: 12, marginTop: 8 }}>
            You will be redirected to the authentication provider.
          </p>
        )}
      </div>
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: b.textDim }}>
        <LockIcon />
        Secure authentication
      </div>
      <Footer />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-spinner { animation: none !important; }
        }
      `}</style>
    </Outer>
  );
}

// ---------------------------------------------------------------------------
// Product branding
// ---------------------------------------------------------------------------

const b = getSiteConfig();

// Inject font link once (module scope, safe — runs once on load)
if (!document.querySelector('link[data-brand-font]')) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = b.fontLink;
  link.setAttribute('data-brand-font', '1');
  document.head.appendChild(link);
}

// ---------------------------------------------------------------------------
// Layout components
// ---------------------------------------------------------------------------

function Outer({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      // Mobile-first: 100dvh tracks the *visible* viewport on mobile (excludes the
      // dynamic browser chrome that 100vh ignores), so the card stays centred and
      // nothing gets clipped under the URL bar. Safe-area insets keep it clear of
      // the notch/home indicator in the installed PWA (viewport-fit=cover).
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
      background: b.bg, fontFamily: b.fontSans, color: b.text, position: 'relative', overflow: 'hidden',
      // Suppress rubber-band bounce on this full-bleed dark shell so the brand
      // background never peeks past the edges (scoped here, not globally on body).
      overscrollBehavior: 'none',
    }}>
      {/* Ambient glow — clamped to the viewport width so it never forces
          horizontal scroll on small phones (a fixed 600px box hangs off-screen). */}
      <div className="brand-glow" style={{
        position: 'fixed', top: '-20%', right: '-10%',
        width: 'min(600px, 120vw)', height: 'min(600px, 120vw)',
        background: `radial-gradient(circle, ${b.accentDim} 0%, transparent 70%)`,
        pointerEvents: 'none', animation: 'brandGlow 8s ease-in-out infinite',
      }} />
      <style>{`@keyframes brandGlow { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:.7;transform:scale(1.1)} }
        @media (prefers-reduced-motion: reduce) { .brand-glow { animation: none !important; } }`}</style>
      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
}

function Header({ tenant }: { tenant: TenantContext | null }) {
  const tagline = tenant ? `${tenant.name} ${b.taglineSuffix}` : b.tagline;
  return (
    <div style={{ textAlign: 'center', marginBottom: 28 }}>
      <Wordmark variant="split" size={26} style={{ marginBottom: 6 }} />
      <div style={{ color: b.textMuted, fontSize: 14, fontWeight: 300 }}>
        {tagline}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div style={{ textAlign: 'center', marginTop: 20 }}>
      <p style={{ fontSize: 11, color: b.textDim, margin: 0 }}>
        Secure &middot; Encrypted &middot; {b.product}
      </p>
      {b.platform === 'patrolschedule' && (
        <p style={{ fontSize: 10, color: b.textDim, opacity: 0.5, margin: '4px 0 0' }}>
          Powered by{' '}
          <a href="https://responsekit.net" target="_blank" rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}>
            ResponseKit
          </a>
        </p>
      )}
    </div>
  );
}

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ verticalAlign: 'middle', marginRight: 4, opacity: 0.6 }}>
      <rect x="4" y="9" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 9V6a3 3 0 116 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
